import { useState } from "react";
import { ArrowLeft, Clock3, Copy, RotateCcw, Search, Share2 } from "lucide-react";
import type { AnalysisResult, GraphNode, TokenCandidate } from "../../server/types";
import { CHAIN_SHORT, compactNumber, currency, relativeTime, riskColor, shortAddress } from "../lib/format";
import { BubbleMap } from "./BubbleMap";
import { Logo } from "./Logo";
import { NodeDetail } from "./NodeDetail";
import { SidePanel } from "./SidePanel";

interface MapScreenProps {
  data: AnalysisResult;
  embedded?: boolean;
  onBack: () => void;
  onRefresh: (token: TokenCandidate) => void;
  onNewSearch: () => void;
}

export function MapScreen({ data, embedded = false, onBack, onRefresh, onNewSearch }: MapScreenProps) {
  const [selected, setSelected] = useState<GraphNode | null>(null);
  const [hiddenClusters, setHiddenClusters] = useState<Set<string>>(new Set());
  const change = data.token.priceChange24h;

  const toggleCluster = (cluster: string) => {
    setHiddenClusters((current) => {
      const next = new Set(current);
      if (next.has(cluster)) next.delete(cluster);
      else next.add(cluster);
      return next;
    });
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) await navigator.share({ title: `${data.token.name} holder map`, url });
    else await navigator.clipboard.writeText(url);
  };

  return (
    <main className={`map-page${embedded ? " map-page--embed" : ""}`}>
      <header className="map-header">
        <button className="icon-button back-button" onClick={onBack}><ArrowLeft /></button>
        <Logo compact />
        <div className="token-chip">
          <span className={`token-avatar token-avatar--${data.token.chain}`}>
            {data.token.imageUrl ? <img src={data.token.imageUrl} alt="" /> : data.token.symbol.slice(0, 2)}
          </span>
          <span className="token-chip-id"><strong>{data.token.name} <b>${data.token.symbol}</b></strong><small>{shortAddress(data.token.address, 6)} <button onClick={() => navigator.clipboard.writeText(data.token.address)}><Copy /></button></small></span>
          <span className={`chain-badge chain-badge--${data.token.chain}`}>{CHAIN_SHORT[data.token.chain]}</span>
        </div>
        <div className="market-chip">
          <span><small>Price</small><strong>{currency(data.token.priceUsd)}</strong></span>
          <b className={change === undefined ? "neutral" : change >= 0 ? "positive" : "negative"}>{change === undefined ? "—" : `${change >= 0 ? "+" : ""}${change.toFixed(2)}%`}</b>
        </div>
        <div className="snapshot-chip"><Clock3 /><span><small>Snapshot</small><strong>{relativeTime(data.snapshotAt)}</strong></span></div>
        <div className="map-header-actions">
          <button onClick={onNewSearch}><Search /><span>New search</span></button>
          <button onClick={() => onRefresh(data.token)}><RotateCcw /></button>
          <button onClick={share}><Share2 /></button>
        </div>
      </header>

      <section className="map-workspace">
        <div className="map-stage">
          <div className="map-metrics">
            <span><small>Top 10</small><strong>{data.stats.top10Percentage.toFixed(1)}%</strong></span>
            <span><small>Largest cluster</small><strong>{data.stats.largestClusterPercentage ? `${data.stats.largestClusterPercentage.toFixed(1)}%` : "None"}</strong></span>
            <span><small>Mapped</small><strong>{data.stats.mappedNodes} · {data.stats.transferConnections} links</strong></span>
            <span className="risk-metric">
              <small>Suspicion / 100</small>
              <strong style={{ color: riskColor(data.stats.riskScore) }} title="Evidence-based suspicion score out of 100; not a probability or a safety guarantee.">
                {`${data.stats.riskLabel} · ${data.stats.riskScore}`}
              </strong>
            </span>
          </div>
          <div className={`map-snapshot-note${data.stale ? " is-stale" : ""}`}>
            {data.stale ? "Saved snapshot — provider temporarily unavailable" : data.stats.limitedCoverage ? "Partial sample — score reflects observed evidence, not a safety guarantee" : "Sampled holder map"} · {relativeTime(data.snapshotAt)}
          </div>
          <BubbleMap data={data} selected={selected} hiddenClusters={hiddenClusters} onSelect={setSelected} />
          {selected && <NodeDetail data={data} node={selected} onClose={() => setSelected(null)} />}
        </div>
        <SidePanel data={data} selected={selected} hiddenClusters={hiddenClusters} onToggleCluster={toggleCluster} onSelect={setSelected} />
      </section>
    </main>
  );
}
